import { styled } from "@linaria/react";
import React from "react";
import { usePatch } from "../../../../api/useCall";
import useInput from "@ui/lib/hooks/useInput";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../../i18n";
import { fractionalToMinutes, minutesToFractional } from "../../../../lib/time/minutes";
import { color } from "@ui";
import ModalForm from "@ui/Modal/Form";
import type { Project } from "../../../../api/useProjects";
import InputV2 from "@ui/Input/InputV2";
import { H4 } from "@ui/Text";
import Switch from "@ui/Switch";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { css } from "@linaria/core";

const ModalFormCustom = styled(ModalForm)`
  gap: 8px;
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const colorCss = css`
  color: ${color.carbon700};
`;

const StyledInputV2 = styled(InputV2)`
  width: 100%;
`;

const Period = styled.div`
  display: flex;
  column-gap: 8px;
  /* > * + * {
    margin-left: 5px;
  } */
`;

interface P {
  project: Project;
  analysisPeriod: number;
  onRequestClose: () => void;
}

export const useAnalysisFrequencyInput = (analysisPeriod: number) => {
  const ONE_YEAR_IN_MINUTE = 365 * 24 * 60;
  const { days, hours, minutes } = minutesToFractional(analysisPeriod);
  const inputDays = useInput(String(days));
  const inputHours = useInput(String(hours));
  const inputMinutes = useInput(String(minutes));

  const minutesBetweenActiveAnalysis = fractionalToMinutes({
    days: parseInt(inputDays.value),
    hours: parseInt(inputHours.value),
    minutes: parseInt(inputMinutes.value),
  });

  const [notUseMinimumInterval, setNotUseMinimumInterval] = React.useState(
    minutesBetweenActiveAnalysis === 0,
  );

  const dirtyMinimumInterval = React.useMemo(
    () =>
      (!notUseMinimumInterval && (inputDays.dirty || inputHours.dirty || inputMinutes.dirty)) ||
      (notUseMinimumInterval && analysisPeriod !== 0),
    [inputDays.dirty, inputHours.dirty, inputMinutes.dirty, notUseMinimumInterval, analysisPeriod],
  );

  const errorInputDays: string | undefined = React.useMemo(() => {
    if (
      inputDays.value === "" ||
      (inputDays.value !== "" && parseInt(inputDays.value) < 0) ||
      !Number.isInteger(parseInt(inputDays.value))
    )
      return t("errors.should_be_positive_integer");

    if (parseInt(inputDays.value) > 365) {
      return t("errors.field_must_be_less_than_1_year");
    }

    return;
  }, [inputDays]);

  const errorInputHours: string | undefined = React.useMemo(() => {
    if (
      inputHours.value === "" ||
      (inputHours.value !== "" &&
        (parseInt(inputHours.value) < 0 || !Number.isInteger(parseInt(inputHours.value))))
    )
      return t("errors.should_be_positive_integer");

    if (
      !(parseInt(inputDays.value) > 365) &&
      parseInt(inputDays.value) * 1440 + parseInt(inputHours.value) * 60 > ONE_YEAR_IN_MINUTE
    ) {
      return t("errors.field_must_be_less_than_1_year");
    }

    return;
  }, [inputHours, inputDays, ONE_YEAR_IN_MINUTE]);

  const errorInputMinutes: string | undefined = React.useMemo(() => {
    if (
      inputMinutes.value === "" ||
      (inputMinutes.value !== "" &&
        (parseInt(inputMinutes.value) < 0 || !Number.isInteger(parseInt(inputMinutes.value))))
    )
      return t("errors.should_be_positive_integer");

    if (
      !(parseInt(inputDays.value) * 1440 + parseInt(inputHours.value) * 60 > ONE_YEAR_IN_MINUTE) &&
      minutesBetweenActiveAnalysis > ONE_YEAR_IN_MINUTE
    ) {
      return t("errors.field_must_be_less_than_1_year");
    }

    return;
  }, [inputDays, inputHours, inputMinutes, minutesBetweenActiveAnalysis, ONE_YEAR_IN_MINUTE]);

  const hasError = errorInputDays || errorInputHours || errorInputMinutes;

  return {
    inputDays,
    inputHours,
    inputMinutes,
    minutesBetweenActiveAnalysis,
    errorInputDays,
    errorInputHours,
    errorInputMinutes,
    dirtyMinimumInterval,
    hasError,
    notUseMinimumInterval,
    setNotUseMinimumInterval,
    ONE_YEAR_IN_MINUTE,
  };
};

const AnalysisFrequencyModalForm = ({ project, analysisPeriod, onRequestClose }: P) => {
  const { addToast } = useToasts();
  const patchProject = usePatch("/projects");
  const {
    inputDays,
    inputHours,
    inputMinutes,
    minutesBetweenActiveAnalysis,
    errorInputDays,
    errorInputHours,
    errorInputMinutes,
    dirtyMinimumInterval: dirty,
    hasError,
    notUseMinimumInterval,
    setNotUseMinimumInterval,
    ONE_YEAR_IN_MINUTE,
  } = useAnalysisFrequencyInput(analysisPeriod);

  const toggleMinimumInterval = React.useCallback(() => {
    setNotUseMinimumInterval(v => !v);
  }, [setNotUseMinimumInterval]);

  const handleSubmit = React.useCallback(async () => {
    if (
      isNaN(parseInt(inputDays.value)) ||
      isNaN(parseInt(inputHours.value)) ||
      isNaN(parseInt(inputMinutes.value))
    ) {
      throw new Error("invalid interval duration, should not happen");
    }
    const minutes = notUseMinimumInterval ? 0 : minutesBetweenActiveAnalysis;
    if (minutes > ONE_YEAR_IN_MINUTE) {
      addToast("error", t("projects.settings.analyses_frequency.modal.submit.error.intervalToBig"));
      return false;
    } else {
      await patchProject({
        body: { ids: [project.id], minutesBetweenActiveAnalysis: minutes },
      });
      return true;
    }
  }, [
    addToast,
    inputDays.value,
    inputHours.value,
    inputMinutes.value,
    patchProject,
    project.id,
    notUseMinimumInterval,
    minutesBetweenActiveAnalysis,
    ONE_YEAR_IN_MINUTE,
  ]);

  return (
    <ModalFormCustom
      onSubmit={dirty && hasError === undefined ? handleSubmit : false}
      onRequestClose={onRequestClose}
      okButtonName={t("modalForm.button.name.modify")}
    >
      <Head>
        <H4 className={colorCss}>{t("projects.settings.analyses_frequency.modal.title")} </H4>
        <Switch checked={notUseMinimumInterval} onChange={toggleMinimumInterval}>
          {t("projects.settings.analyses_frequency.modal.switch.text")}
        </Switch>
      </Head>

      <Period>
        <StyledInputV2
          label={t("projects.settings.analyses_frequency.modal.edit.input.days")}
          data-cy="analysis-frequency-days"
          type="number"
          step={1}
          max={365}
          min={0}
          error={dirty ? errorInputDays : undefined}
          {...inputDays}
          disabled={notUseMinimumInterval}
        />
        <StyledInputV2
          label={t("projects.settings.analyses_frequency.modal.edit.input.hours")}
          data-cy="analysis-frequency-hours"
          type="number"
          step={1}
          max={365 * 24}
          min={0}
          error={dirty ? errorInputHours : undefined}
          {...inputHours}
          disabled={notUseMinimumInterval}
        />
        <StyledInputV2
          label={t("projects.settings.analyses_frequency.modal.edit.input.minutes")}
          data-cy="analysis-frequency-minutes"
          type="number"
          step={1}
          max={365 * 24 * 60}
          min={0}
          error={dirty ? errorInputMinutes : undefined}
          {...inputMinutes}
          disabled={notUseMinimumInterval}
        />
      </Period>
      <AdditionnalMessage
        status="INFO"
        text={t("projects.settings.analyses_frequency.additionnal_message.info.criteria")}
      />
    </ModalFormCustom>
  );
};

export default React.memo(AnalysisFrequencyModalForm);
